/* eslint-disable no-nested-ternary */
/* eslint-disable object-shorthand */
import React, { useEffect, useRef, useState } from "react"
import { useMutation } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, CircularProgress } from "@mui/material"
import Swal from "sweetalert2"
import CvSkill, {
	LocationState,
	PoleLoisirInteretType,
} from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"

const INTERESTS = [
	{ id: "1", name: "Activités extérieures" },
	{ id: "2", name: "Activités manuelles et techniques" },
	{ id: "3", name: "Curiosité intellectuelle et apprentissage" },
	{ id: "4", name: "Science et technologie" },
	{ id: "5", name: "Sens esthétique et expression" },
	{ id: "6", name: "Créativité et conception" },
	{ id: "7", name: "Dévouement aux autres" },
	{ id: "8", name: "Relations personnelles" },
	{ id: "9", name: "Entrepreneur" },
	{ id: "10", name: "Leadership" },
	{ id: "11", name: "Méthodique" },
	{ id: "12", name: "Données et nombres" },
]

function PoleInterets() {
	const [selectedInterests, setSelectedInterests] = useState<string[]>([])
	const [poleLoisirInterets, setPoleLoisirInterets] = useState<
		Array<{ name: string; type: PoleLoisirInteretType }>
	>([])
	const [error, setError] = useState<string | null>(null)
	const [userId, setUserId] = useState<number | null>(null)
	const [cvSkillId, setCvSkillId] = useState<number | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const isSubmittingRef = useRef(false) // Protection double-clic

	const location = useLocation()
	const navigate = useNavigate()
	const { editMode } = (location.state as LocationState) || {
		editMode: false,
	}

	// Effet pour charger les données initiales
	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true)
			try {
				const nav: any = location.state || {}
				const stateUserId = nav?.userId ?? null
				const stateCvSkillIdRaw = nav?.cvSkillId ?? null
				const stateCvSkillId = editMode ? stateCvSkillIdRaw : null

				const lsUserId =
					Number(localStorage.getItem("selectedUserId")) || null
				const lsCvSkillIdRaw =
					Number(localStorage.getItem("selectedCvSkillId")) || null
				const lsCvSkillId = editMode ? lsCvSkillIdRaw : null

				const effectiveUserId = stateUserId ?? lsUserId
				const effectiveCvSkillId = stateCvSkillId ?? lsCvSkillId

				console.log("[PoleInterets] NAV STATE =", nav)
				console.log("[PoleInterets] from LS =", {
					lsUserId,
					lsCvSkillIdRaw,
				})
				console.log("[PoleInterets] editMode =", editMode)
				console.log("[PoleInterets] effective =", {
					effectiveUserId,
					effectiveCvSkillId,
				})

				if (!effectiveUserId) {
					throw new Error("ID utilisateur sélectionné non trouvé")
				}

				setUserId(Number(effectiveUserId))

				if (editMode && effectiveCvSkillId) {
					setCvSkillId(Number(effectiveCvSkillId))
					console.log(
						"[PoleInterets] fetch cv by id",
						effectiveCvSkillId,
					)
					const cvSkill = await cvskillService.getCvSkillById(
						Number(effectiveCvSkillId),
					)
					console.log("[PoleInterets] fetched cv =", cvSkill)

					if (cvSkill?.poleInterets) {
						setSelectedInterests(
							cvSkill.poleInterets.map(i => i.interet),
						)
					}
					if (cvSkill?.poleLoisirInterets) {
						setPoleLoisirInterets(cvSkill.poleLoisirInterets)
					}
				} else {
					// Création : on repart sur les brouillons du user
					const storedInterests = localStorage.getItem(
						`selectedInterests-${effectiveUserId}`,
					)
					if (storedInterests)
						setSelectedInterests(JSON.parse(storedInterests))

					const storedPoleLoisirInterets = localStorage.getItem(
						`poleLoisirInterets-${effectiveUserId}`,
					)
					if (storedPoleLoisirInterets)
						setPoleLoisirInterets(
							JSON.parse(storedPoleLoisirInterets),
						)

					// On s'assure de PAS garder un vieux cvSkillId
					setCvSkillId(null)
				}
			} catch (error) {
				console.error("[PoleInterets] init error:", error)
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			} finally {
				setIsLoading(false)
			}
		}

		fetchData()
	}, [editMode, location.state])

	// Mutation pour créer un nouveau CV Skill
	const createCvSkillMutation = useMutation(
		(newCvSkill: Partial<CvSkill>) => {
			if (!userId) throw new Error("User ID invalide")
			return cvskillService.createCvSkill(newCvSkill as CvSkill, userId)
		},
		{
			onMutate: () => {
				setIsLoading(true)
				isSubmittingRef.current = true
			},
			onSuccess: (data: CvSkill) => {
				if (data.id) {
					localStorage.setItem(
						"selectedCvSkillId",
						data.id.toString(),
					)
					Swal.fire({
						icon: "success",
						title: "CV Skill créé avec succès",
						showConfirmButton: false,
						timer: 1500,
					})
					navigate("/cvskill/cv", {
						state: { userId, cvSkillId: data.id },
					})
				} else {
					setError(
						"Erreur lors de la création du CV Skill: ID non défini",
					)
				}
			},
			onError: (error: any) => {
				setError(
					error.response?.data?.message ||
						"Erreur inconnue lors de la création",
				)
			},
			onSettled: () => {
				setIsLoading(false)
				isSubmittingRef.current = false
			},
		},
	)

	// Mutation pour mettre à jour un CV Skill existant
	const updateCvSkillMutation = useMutation(
		(updatedCvSkill: Partial<CvSkill>) => {
			if (!cvSkillId || !userId)
				throw new Error("CV Skill ID ou User ID invalide")
			return cvskillService.updateCvSkill(
				cvSkillId,
				updatedCvSkill as CvSkill,
				userId,
			)
		},
		{
			onMutate: () => {
				setIsLoading(true)
				isSubmittingRef.current = true
			},
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Intérêts mis à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/cvskill/cv", {
					state: { userId, cvSkillId },
				})
			},
			onError: (error: any) => {
				setError(
					error.response?.data?.message ||
						"Erreur lors de la mise à jour. Veuillez réessayer.",
				)
			},
			onSettled: () => {
				setIsLoading(false)
				isSubmittingRef.current = false
			},
		},
	)

	// Gestion du changement de sélection des intérêts
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target
		let updatedInterests: string[]

		if (checked) {
			if (selectedInterests.length >= 6) {
				setError("Vous ne pouvez sélectionner que 6 choix maximum.")
				return
			}
			updatedInterests = [...selectedInterests, name]
		} else {
			updatedInterests = selectedInterests.filter(item => item !== name)
		}

		setSelectedInterests(updatedInterests)
		setError(null)

		if (!editMode && userId) {
			localStorage.setItem(
				`selectedInterests-${userId}`,
				JSON.stringify(updatedInterests),
			)
		}
	}

	// Gestion de la soumission du formulaire
	const handleSubmit = async () => {
		if (isSubmittingRef.current) {
			console.log("[PoleInterets] ⏳ Soumission déjà en cours, ignoré")
			return
		}

		if (!userId) {
			setError("ID utilisateur manquant. Impossible de continuer.")
			return
		}

		if (selectedInterests.length < 3 || selectedInterests.length > 6) {
			setError("Veuillez sélectionner entre 3 et 6 intérêts.")
			return
		}

		const interetsData = selectedInterests.map(interet => ({ interet }))

		if (editMode && cvSkillId) {
			try {
				const currentCvSkill =
					await cvskillService.getCvSkillById(cvSkillId)
				const updatedCvSkill: Partial<CvSkill> = {
					...currentCvSkill,
					poleInterets: interetsData,
					poleLoisirInterets: poleLoisirInterets,
				}
				updateCvSkillMutation.mutate(updatedCvSkill)
			} catch {
				setError("Erreur lors de la mise à jour. Veuillez réessayer.")
				isSubmittingRef.current = false
			}
		} else {
			const storedAtouts = JSON.parse(
				localStorage.getItem(`selectedAtouts-${userId}`) || "[]",
			)
			const storedPersonalityTraits = JSON.parse(
				localStorage.getItem(`selectedPersonalityTraits-${userId}`) ||
					"[]",
			)
			const storedPersonalityType = JSON.parse(
				localStorage.getItem(`selectedPersonalityType-${userId}`) ||
					"{}",
			)
			const storedAssociatedTraits = JSON.parse(
				localStorage.getItem(`selectedAssociatedTraits-${userId}`) ||
					"[]",
			)
			const storedThemeId =
				localStorage.getItem(`selectedThemeId-${userId}`) || "1"

			if (
				!storedAtouts.length ||
				!storedPersonalityTraits.length ||
				!storedPersonalityType
			) {
				setError(
					"Certaines informations sont manquantes. Veuillez remplir toutes les sections.",
				)
				isSubmittingRef.current = false
				return
			}

			// ✅ NORMALISER poleAtouts : s'assurer que c'est bien un tableau de { atout: string, note?: number }
			const normalizedAtouts = storedAtouts.map((item: any) => {
				// CAS 1: Si c'est déjà au bon format { atout: "...", note: ... }
				if (
					item &&
					typeof item === "object" &&
					typeof item.atout === "string"
				) {
					return {
						atout: item.atout,
						note: item.note || 0,
					}
				}

				// CAS 2: Si atout est un objet avec 'name' { atout: { id: "1", name: "Leadership" } }
				if (
					item &&
					typeof item === "object" &&
					item.atout &&
					typeof item.atout === "object"
				) {
					const atoutName = item.atout.name || String(item.atout)
					return {
						atout: atoutName,
						note: item.note || 0,
					}
				}

				// CAS 3: Si c'est juste un string
				if (typeof item === "string") {
					return { atout: item, note: 0 }
				}

				// CAS 4: Si c'est un objet AtoutType { id: "1", name: "Leadership" }
				if (item && typeof item === "object" && item.name) {
					return {
						atout: item.name,
						note: 0,
					}
				}

				// CAS 5: Fallback - retourner un objet valide
				console.error(
					"[PoleInterets] ⚠️ Format inattendu pour atout:",
					item,
				)
				return { atout: String(item), note: 0 }
			})

			// 🆕 CHANGEMENT CRITIQUE : Envoyer les IDs des traits (pas les noms)
			const newCvSkill: Partial<CvSkill> = {
				poleAtouts: normalizedAtouts,
				poleInterets: interetsData,
				// ✅ Envoi les ID au back (c'est ce qu'il attend maintenant)
				polePersonnaliteTraits: storedPersonalityTraits.map(
					(id: number) => ({ id }),
				),
				polePersonnalitesTypes: [
					{
						id: storedPersonalityType.id,
						personnaliteType:
							storedPersonalityType.personnaliteType,
						associatedTraits: [],
					},
				],
				poleLoisirInterets: poleLoisirInterets,
				withPhoto: false,
				themeId: storedThemeId, // ✅ themeId uniquement au niveau du CV
			}

			console.log("[PoleInterets] === DONNÉES À ENVOYER ===")
			console.log("[PoleInterets] userId:", userId)
			console.log("[PoleInterets] themeId (global):", storedThemeId)
			console.log(
				"[PoleInterets] poleAtouts (normalized):",
				normalizedAtouts,
			)
			console.log("[PoleInterets] poleInterets:", interetsData)
			console.log(
				"[PoleInterets] polePersonnaliteTraits (IDs):",
				storedPersonalityTraits,
			)
			console.log(
				"[PoleInterets] polePersonnalitesTypes:",
				newCvSkill.polePersonnalitesTypes,
			)
			console.log(
				"[PoleInterets] poleLoisirInterets:",
				poleLoisirInterets,
			)
			console.log("[PoleInterets] === FIN DONNÉES ===")
			console.log("storedPersonalityTraits:", storedPersonalityTraits)
			console.log("storedPersonalityType:", storedPersonalityType)

			createCvSkillMutation.mutate(newCvSkill)
		}

		// Nettoyage du localStorage après soumission
		const itemsToRemove = [
			"selectedAtouts",
			"selectedPersonalityTraits",
			"selectedPersonalityType",
			"selectedAssociatedTraits",
			"selectedInterests",
			"poleLoisirInterets",
		]
		itemsToRemove.forEach(item =>
			localStorage.removeItem(`${item}-${userId}`),
		)
	}

	const selectedCount = selectedInterests.length
	const isButtonDisabled =
		isLoading ||
		isSubmittingRef.current ||
		selectedInterests.length < 3 ||
		selectedInterests.length > 6

	if (isLoading && !selectedInterests.length) {
		return (
			<div className="flex justify-center items-center h-screen">
				<CircularProgress style={{ color: "#2daf8e" }} />
			</div>
		)
	}

	return (
		<div className="container mx-auto p-4 max-w-4xl relative overflow-y-auto h-[calc(100vh-8rem)] pb-16">
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                .container {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 20px 24px;
                    border-radius: 16px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                    margin-bottom: 24px;
                }
                
                .page-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0;
                }
                
                .part-badge {
                    display: inline-block;
                    background: white;
                    color: #2daf8e;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 14px;
                    margin-top: 12px;
                }
                
                .selection-info {
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    text-align: center;
                    margin-bottom: 24px;
                }
                
                .selection-counter {
                    background: linear-gradient(135deg, #e0f7f3 0%, #b8ede0 100%);
                    color: #2daf8e;
                    padding: 12px 20px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 16px;
                    text-align: center;
                    margin-bottom: 16px;
                    box-shadow: 0 2px 8px rgba(45, 175, 142, 0.2);
                }
                
                .interest-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .interest-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
                    border-color: #7dd4c1;
                }
                
                .interest-card.selected {
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    border-color: #2daf8e;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                }
                
                .interest-checkbox {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    border: 2px solid #d1d5db;
                    cursor: pointer;
                    accent-color: #2daf8e;
                }
                
                .action-button {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
                    text-transform: none !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    padding: 12px 24px !important;
                    border-radius: 12px !important;
                    width: 100%;
                    transition: all 0.3s ease !important;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
                }
                
                .action-button:disabled {
                    background: #d1d5db !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                    opacity: 0.6;
                }
            `}</style>

			<div className="page-header">
				<h2>{editMode ? "Modifier vos intérêts" : "Mes Intérêts"}</h2>
				<div className="part-badge">Étape finale</div>
			</div>

			{error && (
				<p className="text-red-500 text-center bg-red-50 p-3 rounded-lg mb-4">
					⚠️ {error}
				</p>
			)}

			<div className="selection-counter">
				{selectedCount >= 3 && selectedCount <= 6 ? "✓" : "📊"}{" "}
				{selectedCount} / 3-6 intérêts sélectionnés
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
				{INTERESTS.map(interest => {
					const isSelected = selectedInterests.includes(interest.name)
					return (
						<div
							key={interest.id}
							className={`interest-card ${isSelected ? "selected" : ""}`}
							onClick={e => {
								const checkbox = document.getElementById(
									interest.id,
								) as HTMLInputElement
								if (checkbox) {
									checkbox.checked = !checkbox.checked
									handleChange({
										target: {
											name: interest.name,
											checked: checkbox.checked,
										},
									} as React.ChangeEvent<HTMLInputElement>)
								}
							}}
						>
							<label
								htmlFor={interest.id}
								className="flex items-center space-x-3 cursor-pointer"
							>
								<input
									type="checkbox"
									id={interest.id}
									name={interest.name}
									checked={isSelected}
									onChange={handleChange}
									className="interest-checkbox"
									onClick={e => e.stopPropagation()}
								/>
								<span className="text-gray-800 font-medium text-base flex-1">
									{interest.name}
								</span>
							</label>
						</div>
					)
				})}
			</div>

			<p className="mt-6 font-bold text-center text-red-500 bg-red-50 p-3 rounded-lg">
				⚠️ Entre 3 et 6 choix requis
			</p>

			<div className="flex justify-center mt-8">
				<Button
					onClick={handleSubmit}
					variant="contained"
					className="action-button"
					disabled={isButtonDisabled}
				>
					{isLoading
						? "Enregistrement..."
						: editMode
							? "✓ Mettre à jour les intérêts"
							: "🎉 Finaliser mon CV"}
				</Button>
			</div>
		</div>
	)
}

export default PoleInterets
